import { getLogger } from '@/logger';

const logger = getLogger('utils/browser');

export function getQueryParams() {
  return new URLSearchParams(window.location.search);
}

/**
 * Inserts a script in the document
 */
export async function loadScript({
  url,
  id,
  appendTo = document.body,
  scriptAttrs = {},
  timeout = 5000,
}: {
  url: string;
  id: string;
  appendTo?: HTMLElement;
  scriptAttrs?: { [p: string]: any };
  timeout?: number;
}): Promise<boolean> {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<boolean>((_, reject) => {
    timeoutId = setTimeout(() => {
      logger.debug(`Script timeout: ${url} with id=${id}`);
      reject(`Script timeout: ${url} with id=${id}`);
    }, timeout);
  });

  const loadPromise = new Promise<boolean>((resolve, reject) => {
    const oldTag = document.getElementById(id);
    if (oldTag) {
      resolve(true);
      return;
    }

    if (typeof document.createElement === 'function') {
      try {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        if (scriptAttrs) {
          Object.entries(scriptAttrs).forEach(([key, val]) => {
            script.setAttribute(key, val);
          });
        }
        script.id = id;
        script.addEventListener('load', () => {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          logger.debug(`Script loaded: ${url} with id=${id}`);
          resolve(true);
        });
        script.addEventListener('error', () => {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          logger.error(`Script NOT loaded: ${url} with id=${id}`);
          reject(`Failed to load script: ${id}`);
        });

        logger.debug(`Loading script: ${url} with id=${id}`);
        script.src = url;
        appendTo.appendChild(script);
      } catch (e) {
        reject(e);
      }
    }
  });

  return Promise.race([timeoutPromise, loadPromise]);
}

export function getCurrentPath() {
  let pathname = window.location.pathname;
  if (pathname.charAt(0) === '/' && pathname.length > 1) {
    pathname = pathname.slice(1);
  }
  if (pathname.charAt(pathname.length - 1) === '/' && pathname.length > 1) {
    pathname = pathname.slice(0, pathname.length - 1);
  }
  if (pathname.search(/\./) > 0) {
    pathname = pathname.replace(/(\.[a-z]+$)/, '');
  }
  if (pathname === '/' || pathname === '') {
    pathname = 'home';
  }
  return pathname;
}

export function isStorageAvailable() {
  try {
    const storage = window.localStorage;
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch {
    return false;
  }
}

export async function loadCSS(url: string, id: string, appendTo: HTMLElement = document.head) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve(true);
    } else {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.id = id;
      link.addEventListener('load', () => {
        logger.debug(`css loaded: ${url} with id=${id}`);
        resolve(true);
      });
      link.addEventListener('error', () => {
        logger.error(`css NOT loaded: ${url} with id=${id}`);
        reject(`Failed to load css: ${id}`);
      });

      appendTo.appendChild(link);
    }
  });
}
